class LilyVideoTestimonials extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.cards = [...this.querySelectorAll('[data-testimonial-open]')];
    this.previews = [...this.querySelectorAll('[data-testimonial-preview]')];
    this.modal = this.querySelector('[data-testimonial-modal]');
    this.holder = this.modal?.querySelector('[data-modal-video]');
    this.video = this.holder?.querySelector('video');
    this.modalPoster = this.holder?.querySelector('[data-testimonial-modal-poster]');
    this.packageSelect = this.querySelector('[data-testimonial-package]');
    this.addButton = this.querySelector('[data-testimonial-add]');
    this.status = this.querySelector('[data-testimonial-status]');
    this.productSection = this.closest('[data-lily-product]');
    this.mainRadios = [...(this.productSection?.querySelectorAll('[data-package-radio]') || [])];
    this.activeIndex = 0;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.cards.forEach((card, index) => card.addEventListener('click', () => this.open(index)));
    this.querySelector('[data-testimonial-previous]')?.addEventListener('click', () => this.show(this.activeIndex - 1));
    this.querySelector('[data-testimonial-next]')?.addEventListener('click', () => this.show(this.activeIndex + 1));
    this.packageSelect?.addEventListener('change', this.syncMainPackage);
    this.mainRadios.forEach((radio) => radio.addEventListener('change', this.syncModalPackage));
    this.addButton?.addEventListener('click', this.addToCart);
    this.modal?.addEventListener('lilyeve:modal-closed', this.cleanupModal);
    this.setupPreviews();
    this.syncModalPackage();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.clearLoadFallback();
    this.packageSelect?.removeEventListener('change', this.syncMainPackage);
    this.mainRadios?.forEach((radio) => radio.removeEventListener('change', this.syncModalPackage));
    this.addButton?.removeEventListener('click', this.addToCart);
    this.modal?.removeEventListener('lilyeve:modal-closed', this.cleanupModal);
  }

  setupPreviews() {
    if (this.reduceMotion || !('IntersectionObserver' in window)) return;
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.modal?.hidden !== false) {
          this.playPreview(entry.target);
        } else {
          entry.target.pause();
        }
      });
    }, { threshold: 0.55 });
    this.previews.forEach((video) => this.observer.observe(video));
  }

  playPreview(video) {
    if (!video.getAttribute('src')) {
      const card = video.closest('[data-testimonial-open]');
      const previewSrc = card?.dataset.videoPreviewSrc || card?.dataset.videoSrc;
      if (!previewSrc) return;
      video.src = previewSrc;
      video.load();
    }
    video.muted = true;
    video.play().catch(() => {});
  }

  pausePreviews() {
    this.previews.forEach((video) => video.pause());
  }

  resumeVisiblePreviews() {
    if (this.reduceMotion) return;
    this.previews.forEach((video) => {
      const bounds = video.getBoundingClientRect();
      const horizontallyVisible = bounds.right > 0 && bounds.left < window.innerWidth;
      const visible = bounds.bottom > 0
        && bounds.top < window.innerHeight
        && horizontallyVisible;
      if (visible) this.playPreview(video);
    });
  }

  clearLoadFallback() {
    window.clearTimeout(this.loadTimer);
    this.loadTimer = null;
    if (this.video && this.onSourceReady) {
      this.video.removeEventListener('loadeddata', this.onSourceReady);
    }
    this.onSourceReady = null;
  }

  armLoadFallback() {
    this.clearLoadFallback();
    const fallbackSrc = this.holder?.dataset.videoFallback;
    if (!fallbackSrc || !this.video) return;

    this.onSourceReady = () => {
      if (this.modalPoster) this.modalPoster.hidden = true;
      this.clearLoadFallback();
    };
    this.video.addEventListener('loadeddata', this.onSourceReady, { once: true });
    if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      this.onSourceReady();
      return;
    }
    this.loadTimer = window.setTimeout(() => {
      if (this.modal?.hidden !== false
        || this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA
        || this.video.currentSrc === fallbackSrc) return;
      this.holder.dataset.videoFallbackApplied = 'true';
      this.video.src = fallbackSrc;
      this.video.load();
      this.video.play().catch(() => {});
    }, 2500);
  }

  prepare(index) {
    if (!this.cards.length || !this.video || !this.holder) return false;
    this.activeIndex = (index + this.cards.length) % this.cards.length;
    const card = this.cards[this.activeIndex];
    this.video.pause();
    this.video.poster = card.dataset.videoPoster;
    if (this.modalPoster) {
      this.modalPoster.src = card.dataset.videoPoster;
      this.modalPoster.hidden = false;
    }
    this.holder.dataset.videoSrc = card.dataset.videoSrc;
    this.holder.dataset.videoFallback = card.dataset.videoPreviewSrc || this.holder.dataset.videoFallback;
    delete this.holder.dataset.videoFallbackApplied;
    this.video.src = card.dataset.videoSrc;
    this.video.muted = false;
    this.video.load();
    this.armLoadFallback();
    return true;
  }

  open(index) {
    this.pausePreviews();
    if (!this.prepare(index)) return;
    this.modal?.open();
  }

  show(index) {
    if (!this.prepare(index) || this.modal?.hidden !== false) return;
    this.video.play().catch(() => {});
  }

  syncMainPackage = () => {
    const radio = this.mainRadios.find((item) => item.value === this.packageSelect?.value);
    if (!radio || radio.disabled) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  };

  syncModalPackage = () => {
    const selected = this.mainRadios.find((radio) => radio.checked);
    if (selected && this.packageSelect) this.packageSelect.value = selected.value;
  };

  addToCart = async () => {
    const variantId = this.packageSelect?.value;
    if (!variantId || !window.LilyeveCart || !this.addButton) return;
    this.syncMainPackage();
    this.addButton.disabled = true;
    this.addButton.textContent = 'ADDING...';
    if (this.status) this.status.textContent = '';
    this.modal?.close();

    try {
      await window.LilyeveCart.add(variantId);
    } catch (error) {
      if (this.status) this.status.textContent = error.message || 'Unable to add this package. Please try again.';
      this.modal?.open();
    } finally {
      this.addButton.disabled = false;
      this.addButton.textContent = 'ADD TO CART';
    }
  };

  cleanupModal = () => {
    if (!this.video) return;
    this.clearLoadFallback();
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    requestAnimationFrame(() => this.resumeVisiblePreviews());
  };
}

if (!customElements.get('lily-video-testimonials')) {
  customElements.define('lily-video-testimonials', LilyVideoTestimonials);
}
